import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE_URL = process.env.REACT_APP_API_ENDPOINT || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface CreateStaffBookingRequest {
  staff_email: string;
  staff_name: string;
  date: string;
  date_display: string;
  roles?: string[];  // Optional array of role names
  start_hour?: string;  // Optional start time (e.g., "17:00")
  end_hour?: string;  // Optional end time (e.g., "22:00")
  send_email: boolean;  // Required - whether to send confirmation email
}

export interface StaffBooking {
  booking_id: string;
  staff_email: string;
  staff_name: string;
  date: string;
  date_display: string;
  roles?: string[];  // Optional array of role names
  start_hour?: string;  // Optional start time
  end_hour?: string;  // Optional end time
  confirmed: boolean;
  created_at: string;
  updated_at: string;
}

export interface StaffBookingsFilters {
  date?: string;
  staff_email?: string;
  start_date?: string;
  end_date?: string;
}

export interface StaffBookingsResponse {
  bookings: StaffBooking[];
  count: number;
}

export const createStaffBooking = async (data: CreateStaffBookingRequest): Promise<StaffBooking> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() || '';

    const response = await api.post('/staff-bookings', data, {
      headers: {
        Authorization: token,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error('Error creating staff booking:', error);
    throw new Error(error.response?.data?.error || 'Failed to create staff booking');
  }
};

export const deleteStaffBooking = async (date: string, staffEmail: string): Promise<void> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() || '';

    await api.delete(`/staff-bookings/${encodeURIComponent(date)}/${encodeURIComponent(staffEmail)}`, {
      headers: {
        Authorization: token,
      },
    });
  } catch (error: any) {
    console.error('Error deleting staff booking:', error);
    throw new Error(error.response?.data?.error || 'Failed to delete staff booking');
  }
};

export const getStaffBookings = async (filters?: StaffBookingsFilters): Promise<StaffBookingsResponse> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() || '';

    // Build query parameters
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, String(value));
        }
      });
    }

    const queryString = params.toString();
    const url = `/staff-bookings${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url, {
      headers: {
        Authorization: token,
      },
    });

    return response.data;
  } catch (error: any) {
    console.error('Error fetching staff bookings:', error);
    throw new Error(error.response?.data?.error || 'Failed to fetch staff bookings');
  }
};

export const updateStaffBookingConfirmation = async (
  date: string,
  staffEmail: string,
  confirmed: boolean
): Promise<any> => {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString() || '';

    const response = await api.put(
      `/staff-bookings/${encodeURIComponent(date)}/${encodeURIComponent(staffEmail)}`,
      { confirmed },
      {
        headers: {
          Authorization: token,
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  } catch (error: any) {
    console.error('Error updating staff booking confirmation:', error);
    throw new Error(error.response?.data?.error || 'Failed to update staff booking confirmation');
  }
};
